/**
 * Customer Dead Lead List Hook
 */
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getCustomerDeadLead } from '@/modules/Customer/services'
import {
    getDeadLeadColumns,
    getDeadLeadFilterItems,
    DEAD_LEAD_FILTER_DEFAULTS,
    DEAD_LEAD_SORT_CONFIG,
} from '../utils/customer-dead-lead-config'

interface DeadLeadFilters {
    customer_sale_followers: number[]
    customer_industry: string[]
    customer_service_interestings: string[]
    customer_created_at: string[]
    customer_source: string[]
    customer_source_type: string[]
}

export const useCustomerDeadLeadList = () => {
    const { t } = useTranslation('customer')

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<DeadLeadFilters>({
        defaults: {
            page: 1,
            page_size: 20,
            ...DEAD_LEAD_SORT_CONFIG.defaultSort,
        },
        filterDefaults: DEAD_LEAD_FILTER_DEFAULTS,
    })

    const { control } = useForm<DeadLeadFilters>({
        defaultValues: filters,
        values: filters,
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, 'dead-lead', queryParams],
        queryFn: () =>
            getCustomerDeadLead({
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                sort_by: params.sort_by,
                sort_order: params.sort_order,
                filters: queryParams.filters,
            }),
        placeholderData: true,
    })

    const customers = data?.results || []
    const pagination = data?.pagination

    const columns = useMemo(() => getDeadLeadColumns(t), [t])
    const filterItems = useMemo(() => getDeadLeadFilterItems(t), [t])

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    return {
        customers,
        pagination,
        stateComponent: renderState(),
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        filterItems,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        t,
    }
}
